import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { KeyValue, Pill, Field, SelectInput, TextArea } from '../../components/ui';
import type { Tone } from '../../lib/format';
import { SearchSelect } from '../../components/SearchSelect';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { api, ApiError } from '../../lib/api';
import { SerialLookupPage } from './SerialLookupPage';

// ③-1 · 历史序列号关联工作台（ERPNext 序列号承接台账的人工核查入口）
//
// 口径（2026-10 定案）：
//   · 只补【单据级】关联（交货单 / 合同 / 库存单据），不批量建立工件身份；
//   · ERPNext 原始字段只读展示，任何核查动作都不改写；
//   · 核查状态（待核查 / 已确认关联 / 已核查暂无对应单据 / 存在冲突）与 ERPNext
//     的 Delivered/Active/Inactive 分开显示，不混用；
//   · 确认必须带依据，后端记录操作人/时间/前后值/记录版本；
//   · 物料不一致、来源冲突、多候选 → 后端 409 阻断，必须显式确认冲突才能定案；
//   · 版本过期提交 → 409「已被他人修改」，不覆盖他人判断；
//   · 候选只作参考，绝不自动选中；同号多记录分别展示，不自动合并；
//   · 身份回链只在历史件【实际再次送修】并人工确认身份后建立。
//
// 分页为什么自己做而不是复用 ListPage：本页筛选维度多（源端状态 / 核查状态 / 来源单据
// 类型 / 解析注记 / 已关联与否），且必须服务端分页（全量 10,081 条不能一次拉）。

interface LegacySerialRow {
  id: string;
  serial_no: string;
  item_code?: string | null;
  item_id?: string | null;
  warehouse_name?: string | null;
  batch_no?: string | null;
  status?: string | null;
  company?: string | null;
  purchase_document_type?: string | null;
  purchase_document_no?: string | null;
  purchase_date?: string | null;
  delivery_document_type?: string | null;
  delivery_document_no?: string | null;
  delivery_date?: string | null;
  delivery_id?: string | null;
  contract_id?: string | null;
  stock_movement_id?: string | null;
  resolution_note?: string | null;
  review_status: string;
  review_label?: string;
  review_version: number;
  reviewed_at?: string | null;
  reviewed_by_name?: string | null;
  has_delivery_link: boolean;
  has_stock_movement_link: boolean;
  has_any_link: boolean;
}

interface LinkedDoc {
  delivery: { id: string; number: string; status: string; delivery_date: string; contract_number?: string | null; contract_id?: string | null } | null;
  contract: { id: string; number: string; status: string } | null;
  item: { id: string; code: string; name_zh?: string | null; name_es?: string | null } | null;
  movement: { id: string; number: string; movement_type: string; movement_date: string; status: string; source_id?: string | null } | null;
  supplier: { id: string; name: string; code?: string | null } | null;
}

interface Candidate {
  kind: 'delivery' | 'stock_movement';
  confidence: 'source_document_no' | 'deterministic_source_id' | 'same_item_recent';
  id: string;
  number: string;
  status?: string;
  movement_type?: string;
  movement_date?: string;
  delivery_date?: string;
}

interface AssetLink {
  id: string;
  asset_id: string;
  service_cycle_id?: string | null;
  customer_serial?: string | null;
  internal_code?: string | null;
  asset_status?: string | null;
  customer_name?: string | null;
  evidence: string;
  actor_name?: string | null;
  created_at: string;
  revoked_at?: string | null;
}

interface ReviewRecord {
  id: string;
  action: string;
  from_status?: string | null;
  to_status?: string | null;
  evidence: string;
  actor_name?: string | null;
  record_version: number;
  created_at: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
}

interface LegacySerialDetail extends LegacySerialRow {
  source_record: Record<string, string | null>;
  linked_documents: LinkedDoc;
  asset_links: AssetLink[];
  candidates: Candidate[];
  review_history: ReviewRecord[];
  delivery?: LinkedDoc['delivery'];
  item?: LinkedDoc['item'];
  contract?: LinkedDoc['contract'];
  stock_movement?: LinkedDoc['movement'];
}

interface ListResponse {
  rows: LegacySerialRow[];
  total: number;
  resolved: number;
  unlinked: number;
  summary?: Record<string, number>;
  review_status_labels?: Record<string, string>;
}

const PAGE_SIZE = 50;

const REVIEW_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'pending_review', label: '待核查' },
  { value: 'confirmed_link', label: '已确认关联' },
  { value: 'verified_no_document', label: '已核查、暂无对应单据' },
  { value: 'conflict', label: '存在冲突' },
];

const REVIEW_TONES: Record<string, Tone> = {
  pending_review: 'warn',
  confirmed_link: 'ok',
  verified_no_document: 'info',
  conflict: 'bad',
};

const ACTION_LABELS: Record<string, string> = {
  confirm_link: '确认关联交货单',
  confirm_stock_movement_link: '确认关联库存单据',
  clear_link: '解除关联',
  mark_verified_no_doc: '标记已核查、暂无对应单据',
  resolve_stock_movement: '确定性解析库存单据',
  link_asset: '建立工件身份回链',
  unlink_asset: '撤销工件身份回链',
};

const CONFIDENCE_LABELS: Record<string, string> = {
  source_document_no: '源端单号与 Integral 单据号精确相同',
  deterministic_source_id: '库存流水 source_id 与源端单号精确相同',
  same_item_recent: '同物料近期单据（弱候选：物料相同不足以认定同一实物）',
};

function reviewPill(row: { review_status: string; review_label?: string }) {
  return <Pill tone={REVIEW_TONES[row.review_status] || 'info'} label={row.review_label || row.review_status} />;
}

/** 单据链接：带唯一业务单号跳转，落到具体那一张单，而不是模糊搜索结果页。 */
function DocumentLink({ kind, number }: { kind: 'delivery' | 'contract' | 'stock'; number: string }) {
  const to = kind === 'delivery'
    ? `/deliveries?q=${encodeURIComponent(number)}`
    : kind === 'contract'
      ? `/contracts?q=${encodeURIComponent(number)}`
      : `/stock?tab=ledger&q=${encodeURIComponent(number)}`;
  return <Link className="num" to={to}>{number}</Link>;
}

function ConfirmPanel({ row, onDone }: { row: LegacySerialDetail; onDone: () => void }) {
  const [kind, setKind] = useState<'none' | 'delivery' | 'stock_movement'>(
    row.delivery_id ? 'delivery' : row.stock_movement_id ? 'stock_movement' : 'none',
  );
  const [deliveryId, setDeliveryId] = useState<string | null>(row.delivery_id ?? null);
  const [deliveryLabel, setDeliveryLabel] = useState(row.linked_documents.delivery?.number ?? '');
  const [movementId, setMovementId] = useState<string | null>(row.stock_movement_id ?? null);
  const [evidence, setEvidence] = useState('');
  const [acknowledge, setAcknowledge] = useState(false);
  const [conflicts, setConflicts] = useState<{ code: string; message: string }[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(null); setOk(null); setConflicts(null); setBusy(true);
    const body: Record<string, unknown> = {
      evidence: evidence.trim(),
      version: row.review_version,
      acknowledge_conflict: acknowledge,
    };
    if (kind === 'delivery') body.delivery_id = deliveryId;
    else if (kind === 'stock_movement') body.stock_movement_id = movementId;
    else body.delivery_id = null;
    try {
      const res = await api.post<{ data: { replayed?: boolean } }>(`/api/migration/legacy-serials/${row.id}/confirm-link`, body);
      setOk(res?.data?.replayed ? '与现有核查结果相同，未重复记录（幂等）' : '已提交核查，前后值与依据已留痕');
      setTimeout(onDone, 700);
    } catch (e) {
      if (e instanceof ApiError) {
        const details = e.details as { conflicts?: { code: string; message: string }[] } | undefined;
        if (e.code === 'conflict_detected' && details?.conflicts) setConflicts(details.conflicts);
        setErr(e.message);
      } else setErr('操作失败');
    } finally { setBusy(false); }
  };

  const markVerified = async () => {
    setErr(null); setOk(null); setBusy(true);
    try {
      await api.post(`/api/migration/legacy-serials/${row.id}/mark-verified`,
        { evidence: evidence.trim(), version: row.review_version });
      setOk('已标记「已核查、暂无对应单据」');
      setTimeout(onDone, 700);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '操作失败');
    } finally { setBusy(false); }
  };

  const usable = kind === 'delivery' ? !!deliveryId : kind === 'stock_movement' ? !!movementId : true;

  return (
    <div className="notice warn" style={{ display: 'block' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        人工确认（仅管理员 · 需填依据 · 前后值与记录版本进核查历史与审计）
      </div>
      <div style={{ fontSize: 12, marginBottom: 8, color: 'var(--ink-3)' }}>
        只写 Integral 关联字段；ERPNext 原始字段不会被改动。选择交货单后合同由该交货单带出，
        不接受单独填写的合同。当前记录版本 V{row.review_version}，过期提交会被拒绝（避免覆盖他人核查）。
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="关联类型">
          <SelectInput value={kind} onChange={(e) => setKind(e.target.value as 'none' | 'delivery' | 'stock_movement')}>
            <option value="none">不关联（解除）</option>
            <option value="delivery">交货单</option>
            <option value="stock_movement">库存单据（Stock Entry 承接）</option>
          </SelectInput>
        </Field>
        {kind === 'delivery' ? (
          <Field label="关联交货单（按单号检索）">
            <SearchSelect<{ id: string; number?: string; status?: string; delivery_date?: string }>
              endpoint="/api/deliveries"
              value={deliveryId}
              label={deliveryLabel}
              limit={20}
              paginated
              optionTitle={(d) => d.number ?? d.id}
              optionMeta={(d) => `${d.status ?? ''}${d.delivery_date ? ' · ' + d.delivery_date : ''}`}
              placeholder="按交货单号检索（如 ML-DN-2023-…）…"
              onChange={(d) => { setDeliveryId(d?.id ?? null); setDeliveryLabel(d ? (d.number ?? '') : ''); }}
            />
          </Field>
        ) : null}
        {kind === 'stock_movement' ? (
          <Field label="关联库存单据（从候选选择或粘贴 id）">
            <input
              value={movementId ?? ''}
              onChange={(e) => setMovementId(e.target.value || null)}
              placeholder="库存流水 id"
              style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, minWidth: 220 }}
            />
          </Field>
        ) : null}
        <Field label="核查依据（必填）" span>
          <TextArea
            value={evidence}
            placeholder="写明核实来源与理由，如：ERPNext DN 单 ML-DN-2023-00008 与本序列号 delivery_document_no 一致，客户与物料核对无误"
            onChange={(e) => setEvidence(e.target.value)}
          />
        </Field>
      </div>

      {conflicts ? (
        <div className="notice bad" style={{ marginTop: 8, display: 'block' }}>
          <b>检测到冲突，已被阻断：</b>
          <ul style={{ margin: '4px 0 0 18px' }}>
            {conflicts.map((c, i) => <li key={i}>{c.message}</li>)}
          </ul>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
            <input type="checkbox" checked={acknowledge} onChange={(e) => setAcknowledge(e.target.checked)} />
            我已核实上述冲突与本序列号的实际情况，确认按此依据定案
          </label>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <button className="btn primary" disabled={busy || !evidence.trim() || !usable} onClick={submit}>
          {busy ? '提交中…' : kind === 'none' ? '解除关联（置空）' : '确认关联'}
        </button>
        <button
          className="btn"
          disabled={busy || !evidence.trim()}
          onClick={markVerified}
          title="来源确实没有可依单据时使用（如 Stock Entry 领料/消耗）"
        >
          标记已核查、暂无对应单据
        </button>
      </div>
      {err ? <div className="notice bad" style={{ marginTop: 8 }}>{err}</div> : null}
      {ok ? <div className="notice ok" style={{ marginTop: 8 }}>{ok}</div> : null}
    </div>
  );
}

function Detail({ row, canWrite, onChanged }: { row: LegacySerialDetail; canWrite: boolean; onChanged: () => void }) {
  const linked = row.linked_documents;
  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{row.serial_no}</span>
        <h3>{`${row.item?.code ?? row.item_code ?? ''} ${row.item?.name_zh ?? row.item?.name_es ?? ''}`.trim() || '—'}</h3>
        {reviewPill(row)}
        <div className="spacer" />
        <span className="muted" style={{ fontSize: 12 }}>记录版本 V{row.review_version}</span>
      </div>

      {/* ① 源端原始记录（只读，核查不改写） */}
      <h4 style={{ margin: '10px 0 4px', fontSize: 13 }}>① ERPNext 源端原始记录（只读，核查动作不改写）</h4>
      <div className="kv-grid" style={{ marginBottom: 8 }}>
        <KeyValue k="ERPNext 物料编码">{row.source_record.item_code || '—'}</KeyValue>
        <KeyValue k="ERPNext 状态">{row.source_record.status || '—'}</KeyValue>
        <KeyValue k="批次">{row.source_record.batch_no || '—'}</KeyValue>
        <KeyValue k="仓库">{row.source_record.warehouse_name || '—'}</KeyValue>
        <KeyValue k="采购单据">{[row.source_record.purchase_document_type, row.source_record.purchase_document_no].filter(Boolean).join(' ') || '—'}</KeyValue>
        <KeyValue k="采购日期">{row.source_record.purchase_date || '—'}</KeyValue>
        <KeyValue k="交付单据（原始）">{`${row.source_record.delivery_document_type ?? ''} ${row.source_record.delivery_document_no ?? ''}`.trim() || '—'}</KeyValue>
        <KeyValue k="交付日期（原始）">{row.source_record.delivery_date || '—'}</KeyValue>
        <KeyValue k="解析注记（导入期）">{row.source_record.resolution_note || '—'}</KeyValue>
      </div>

      {/* ② Integral 已关联单据 */}
      <h4 style={{ margin: '10px 0 4px', fontSize: 13 }}>② Integral 已关联单据</h4>
      <div className="kv-grid" style={{ marginBottom: 8 }}>
        <KeyValue k="交货单">
          {linked.delivery ? <DocumentLink kind="delivery" number={linked.delivery.number} /> : <span className="muted">未关联</span>}
        </KeyValue>
        <KeyValue k="合同（经交货单带出）">
          {linked.contract ? <DocumentLink kind="contract" number={linked.contract.number} /> : <span className="muted">未关联</span>}
        </KeyValue>
        <KeyValue k="库存单据">
          {linked.movement
            ? <span><DocumentLink kind="stock" number={linked.movement.number} /> <span className="muted">· {linked.movement.movement_type} · {linked.movement.source_id || ''}</span></span>
            : <span className="muted">未关联</span>}
        </KeyValue>
        <KeyValue k="物料档案">{linked.item ? `${linked.item.code} ${linked.item.name_zh ?? ''}` : <span className="muted">未解析</span>}</KeyValue>
        <KeyValue k="供应商">{linked.supplier ? linked.supplier.name : <span className="muted">未解析</span>}</KeyValue>
        <KeyValue k="最近核查">{row.reviewed_at ? `${row.reviewed_at} · ${row.reviewed_by_name || '—'}` : '—'}</KeyValue>
      </div>

      {/* ③ 候选与人工确认 */}
      <h4 style={{ margin: '10px 0 4px', fontSize: 13 }}>③ 候选（仅供参考，不会自动选中）与人工确认</h4>
      {row.candidates.length === 0 ? (
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>无可参考候选。候选不足时请按 ERPNext 原始单据人工核查。</div>
      ) : (
        <div style={{ marginBottom: 8 }}>
          {row.candidates.map((c) => (
            <div key={`${c.kind}:${c.id}`} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, marginBottom: 6, background: '#fff' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <Pill tone={c.confidence === 'same_item_recent' ? 'warn' : 'info'} label={c.kind === 'delivery' ? '交货单候选' : '库存单据候选'} />
                <b className="num">{c.number}</b>
                <span className="muted" style={{ fontSize: 12 }}>
                  {c.kind === 'delivery' ? `${c.status ?? ''} ${c.delivery_date ?? ''}` : `${c.movement_type ?? ''} ${c.movement_date ?? ''}`}
                </span>
              </div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{CONFIDENCE_LABELS[c.confidence] || c.confidence}</div>
            </div>
          ))}
        </div>
      )}

      {canWrite ? (
        <>
          <ConfirmPanel row={row} onDone={onChanged} />
          <LegacyAssetLinks row={row} onChanged={onChanged} />
        </>
      ) : (
        <div className="notice" style={{ background: 'var(--panel-inset)' }}>
          确认关联仅管理员可操作；如需调整请联系管理员并提供核实依据。
        </div>
      )}

      {/* ④ 核查历史 */}
      <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>④ 核查历史（append-only，撤销与更正只新增，不删除旧判断）</h4>
      {row.review_history.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>尚无人工核查记录。</div>
      ) : (
        <table className="lines" style={{ fontSize: 12 }}>
          <thead>
            <tr><th>时间</th><th>动作</th><th>状态变化</th><th>操作人</th><th>版本</th><th>依据</th></tr>
          </thead>
          <tbody>
            {row.review_history.map((r) => (
              <tr key={r.id}>
                <td className="num">{r.created_at}</td>
                <td>{ACTION_LABELS[r.action] || r.action}</td>
                <td className="muted">{`${r.from_status ?? '—'} → ${r.to_status ?? '—'}`}</td>
                <td>{r.actor_name || '—'}</td>
                <td className="num">V{r.record_version}</td>
                <td style={{ maxWidth: 380, whiteSpace: 'pre-wrap' }}>{r.evidence}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LegacyAssetLinks({ row, onChanged }: { row: LegacySerialDetail; onChanged: () => void }) {
  const [assetId, setAssetId] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const link = async () => {
    setErr(null); setBusy(true);
    try {
      await api.post(`/api/migration/legacy-serials/${row.id}/link-asset`, { asset_id: assetId.trim(), service_cycle_id: cycleId.trim(), evidence: evidence.trim(), version: row.review_version });
      setAssetId(''); setCycleId(''); setEvidence('');
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '操作失败');
    } finally { setBusy(false); }
  };
  const unlink = async (linkId: string) => {
    setErr(null); setBusy(true);
    try {
      await api.post(`/api/migration/legacy-serials/${row.id}/unlink-asset`,
        { link_id: linkId, evidence: evidence.trim(), version: row.review_version });
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '操作失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="notice" style={{ display: 'block', marginTop: 8 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>历史件身份回链（仅在实际再次送修、人工确认身份后建立）</div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 6 }}>
        不凭同号自动合并；同一序列号可对应多个工件身份。回链不创建伪造的历史履历 ——
        本次维修链仍由收件登记产生的维修版本承载。
      </div>
      {row.asset_links.length === 0 ? <div className="muted" style={{ fontSize: 12 }}>暂无回链。</div> : (
        <table className="lines" style={{ fontSize: 12 }}>
          <thead><tr><th>工件</th><th>客户序列号</th><th>履历</th><th>依据</th><th>状态</th><th /></tr></thead>
          <tbody>
            {row.asset_links.map((l) => (
              <tr key={l.id}>
                <td><Link to={`/asset-traceability?asset=${encodeURIComponent(l.asset_id)}`}>{l.internal_code || l.asset_id.slice(0, 8)}</Link></td>
                <td className="num">{l.customer_serial || '—'}</td>
                <td className="muted">{l.service_cycle_id ? '已关联本次送修履历' : '未关联履历'}</td>
                <td style={{ maxWidth: 300 }}>{l.evidence}</td>
                <td>{l.revoked_at ? <Pill tone="warn" label="已撤销" /> : <Pill tone="ok" label="有效" />}</td>
                <td>{!l.revoked_at ? <button className="btn ghost" disabled={busy || !evidence.trim()} onClick={() => unlink(l.id)}>撤销</button> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
        <Field label="工件 asset_id（收件登记产生的工件身份）">
          <input
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            placeholder="工件 id"
            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, minWidth: 240 }}
          />
        </Field>
        <Field label="本次送修履历（必填）">
          <input value={cycleId} onChange={(e) => setCycleId(e.target.value)} placeholder="本次履历 id" />
        </Field>
        <Field label="依据（建立或撤销均必填）" span>
          <TextArea value={evidence} placeholder="如：2026-10-01 收件登记，实物序列号与本次送修件核对一致" onChange={(e) => setEvidence(e.target.value)} />
        </Field>
        <button className="btn" disabled={busy || !assetId.trim() || !cycleId.trim() || !evidence.trim()} onClick={link}>建立回链</button>
      </div>
      {err ? <div className="notice bad" style={{ marginTop: 8 }}>{err}</div> : null}
    </div>
  );
}

export function LegacySerialsPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const canWrite = can(user, 'physical-assets', 'edit');

  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [resolution, setResolution] = useState('');
  const [linked, setLinked] = useState('');
  const [page, setPage] = useState(0);

  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<LegacySerialDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const seq = useRef(0);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', String(PAGE_SIZE));
    p.set('offset', String(page * PAGE_SIZE));
    if (searchParams.get('serial_id')) p.set('serial_id', searchParams.get('serial_id')!);
    if (q.trim()) p.set('q', q.trim());
    if (status) p.set('status', status);
    if (reviewStatus) p.set('review_status', reviewStatus);
    if (sourceType) p.set('source_doc_type', sourceType);
    if (resolution) p.set('resolution', resolution);
    if (linked) p.set('linked', linked);
    return p.toString();
  }, [q, status, reviewStatus, sourceType, resolution, linked, page, searchParams]);

  const load = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true); setError(null);
    try {
      const res = await api.get<{ data: ListResponse }>(`/api/migration/legacy-serials?${query}`);
      if (my !== seq.current) return;
      setData(res.data);
    } catch (e) {
      if (my !== seq.current) return;
      setError(e instanceof ApiError ? e.message : '加载失败');
      setData(null);
    } finally { if (my === seq.current) setLoading(false); }
  }, [query]);

  // 关键字 300ms 防抖，其余筛选立即生效；筛选变化一律回到第 1 页。
  useEffect(() => {
    const t = setTimeout(() => { void load(); }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const resetPage = () => setPage(0);
  const change = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setter(e.target.value);
    resetPage();
  };

  const openDetail = async (id: string) => {
    if (expandedId === id) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await api.get<{ data: LegacySerialDetail }>(`/api/migration/legacy-serials/${id}`);
      setDetail(res.data);
    } catch {
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  useEffect(() => {
    const id = searchParams.get('serial_id');
    if (id) void openDetail(id);
  }, [searchParams]);

  const refreshDetail = async () => {
    await load();
    if (expandedId) {
      try {
        const res = await api.get<{ data: LegacySerialDetail }>(`/api/migration/legacy-serials/${expandedId}`);
        setDetail(res.data);
      } catch { /* 保持旧详情，不掩盖列表刷新结果 */ }
    }
  };

  const total = data?.total ?? 0;
  const pageStart = page * PAGE_SIZE;

  return (
    <div>
      <SerialLookupPage />
      <details style={{ marginTop: 16 }}>
        <summary style={{ cursor: 'pointer', padding: '10px 4px', fontWeight: 600 }}>ERPNext 历史单据关联管理</summary>
    <section className="panel">
      <div className="panel-head">
        <div style={{ minWidth: 0 }}>
          <h2>ERPNext 历史单据关联管理</h2>
          <div className="desc">
            ERPNext 库存序列号承接台账（10,081 条）· 只补单据级关联，不批量建工件身份；
            仅用于管理员补充 ERPNext 原始记录与 Integral 合同、交货、库存单据之间的链接；不代表维修履历状态。
          </div>
        </div>
        <div className="spacer" />
      </div>

      <div className="toolbar" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="关键字（序列号 / 物料 / 源单号 / 批次）">
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); resetPage(); }}
            placeholder="搜索序列号 / 物料编码 / 交货单号 / 采购单号 / 批次…"
            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, minWidth: 260 }}
          />
        </Field>
        <Field label="ERPNext 状态">
          <SelectInput value={status} onChange={change(setStatus)}>
            <option value="">全部</option>
            <option value="Delivered">Delivered</option>
            <option value="Inactive">Inactive</option>
            <option value="Active">Active</option>
          </SelectInput>
        </Field>
        <Field label="核查状态">
          <SelectInput value={reviewStatus} onChange={change(setReviewStatus)}>
            {REVIEW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </SelectInput>
        </Field>
        <Field label="来源单据类型">
          <SelectInput value={sourceType} onChange={change(setSourceType)}>
            <option value="">全部</option>
            <option value="delivery_note">Delivery Note</option>
            <option value="stock_entry">Stock Entry</option>
            <option value="none">无交付单号</option>
          </SelectInput>
        </Field>
        <Field label="解析注记">
          <SelectInput value={resolution} onChange={change(setResolution)}>
            <option value="">全部</option>
            <option value="unresolved">未解析出交货单</option>
            <option value="item_issue">物料解析异常</option>
            <option value="any">有注记</option>
            <option value="none">无注记</option>
          </SelectInput>
        </Field>
        <Field label="关联情况">
          <SelectInput value={linked} onChange={change(setLinked)}>
            <option value="">全部</option>
            <option value="0">未关联</option>
            <option value="1">已关联</option>
          </SelectInput>
        </Field>
        <button className="btn" onClick={() => void load()} disabled={loading}>{loading ? '加载中…' : '刷新'}</button>
      </div>

      <div style={{ fontSize: 12.5, color: '#475569', margin: '8px 0' }}>
        当前筛选命中 <b>{total}</b> 条，已关联 <b>{data?.resolved ?? 0}</b> 条，未关联 <b>{data?.unlinked ?? 0}</b> 条
        （服务端分页，每页 {PAGE_SIZE} 条）
      </div>

      {error ? <div className="notice bad">{error}</div> : null}

      <table className="lines" style={{ fontSize: 12.5 }}>
        <thead>
          <tr>
            <th>序列号</th><th>物料</th><th>ERPNext 状态</th><th>批次</th>
            <th>源端交付单据</th><th>Integral 关联</th><th>历史状态 / 关联核查</th><th />
          </tr>
        </thead>
        <tbody>
          {(data?.rows || []).map((r) => (
            <tr key={r.id}>
              <td className="num strong">{r.serial_no}</td>
              <td>{`${r.item_code ?? ''}` || '—'}</td>
              <td>{r.status || '—'}</td>
              <td>{r.batch_no || '—'}</td>
              <td>
                {r.delivery_document_no
                  ? <span className="num">{`${r.delivery_document_type === 'Stock Entry' ? 'Stock Entry ' : ''}${r.delivery_document_no}`}</span>
                  : <span className="muted">—</span>}
              </td>
              <td>
                {r.has_delivery_link ? <span className="num muted">交货单已关联</span>
                  : r.has_stock_movement_link ? <span className="num muted">库存单据已关联</span>
                    : <span className="muted">未关联</span>}
              </td>
              <td>{!String(r.delivery_date || '').startsWith('2026-10') && !String(r.purchase_date || '').startsWith('2026-10')
                ? <Pill tone="ok" label="已完工（ERPNext历史）" />
                : reviewPill(r)}</td>
              <td>
                <button className="btn ghost" onClick={() => void openDetail(r.id)}>
                  {expandedId === r.id ? '收起' : '核查'}
                </button>
              </td>
            </tr>
          ))}
          {!loading && (data?.rows || []).length === 0 ? (
            <tr><td colSpan={8} className="muted" style={{ padding: 12 }}>无匹配记录</td></tr>
          ) : null}
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '10px 0' }}>
        <button className="btn" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>上一页</button>
        <span className="muted" style={{ fontSize: 12 }}>
          第 {pageStart + 1}–{pageStart + (data?.rows.length ?? 0)} 条 / 共 {total} 条
        </span>
        <button className="btn" disabled={pageStart + (data?.rows.length ?? 0) >= total} onClick={() => setPage((p) => p + 1)}>下一页</button>
      </div>

      {expandedId ? (
        <div style={{ marginTop: 8 }}>
          {detailLoading && <div className="muted" style={{ fontSize: 12 }}>详情加载中…</div>}
          {detail ? <Detail row={detail} canWrite={canWrite} onChanged={() => void refreshDetail()} /> : null}
        </div>
      ) : null}
    </section>
      </details>
    </div>
  );
}
